import React, { useState, useEffect } from "react";
import { Phone, Mail, MapPin, Clock, Send } from "lucide-react";

// =============================
// Intégration Formspree (mode statique)
// Form ID courant : xvgwkbge – configurable via variable d'environnement.
// =============================
const FORMSPREE_ENDPOINT = process.env.REACT_APP_FORMSPREE_ENDPOINT || "https://formspree.io/f/xvgwkbge";

// NOTE: L'ancien envoi via backend (API /public/contact) a été retiré pour le mode 100% statique OVH.
// Pour réactiver plus tard : réintroduire un POST axios/fetch vers votre API FastAPI.

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    projectType: "",
    message: "",
    consent: false
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [honeypot, setHoneypot] = useState(""); // champ anti-spam
  const [contactInfo, setContactInfo] = useState({
    phone: "07 65 63 25 84",
    email: "contact@ctb28.fr",
    address: "Essonne (91), Loiret (45) et Eure-et-Loir (28)",
    hours: "Lun-Ven : 9h-17h"
  });

  // Les informations de contact sont maintenant définies localement
  // Plus besoin de les charger depuis l'API

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMsg("");

    // Validation explicite (renforce l'attribut required HTML et empêche envoi si un script/UA le contourne)
    if (!formData.name || !formData.email || !formData.phone || !formData.projectType || !formData.message) {
      setErrorMsg("Veuillez remplir tous les champs obligatoires.");
      setIsLoading(false);
      return;
    }
    if (!formData.consent) {
      setErrorMsg("Vous devez accepter la politique de confidentialité pour envoyer votre demande.");
      setIsLoading(false);
      return;
    }

    // Anti-spam : si le honeypot est rempli on stoppe silencieusement
    if (honeypot) {
      setIsLoading(false);
      return;
    }

    if (!FORMSPREE_ENDPOINT) {
      setErrorMsg("Formulaire non configuré : variable REACT_APP_FORMSPREE_ENDPOINT absente.");
      setIsLoading(false);
      return;
    }

    try {
      // Premier essai : JSON
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        projectType: formData.projectType,
        message: formData.message,
        consent: formData.consent ? 'oui' : 'non',
        // Sujet plus riche : inclut le type de projet et le nom pour lecture rapide dans la boîte mail
        _subject: `Contact CTB28 | ${formData.projectType || 'Demande'} | ${formData.name || 'Visiteur'}`,
        _replyto: formData.email,
        submitted_at: new Date().toISOString()
      };

      let res = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      let data = await res.json().catch(() => ({}));

      // Fallback si statut 403 : souvent dû à restrictions ou format non accepté selon configuration
      if (res.status === 403) {
        console.warn('[Formspree] 403 sur JSON, tentative fallback FormData');
        const fd = new FormData();
        Object.entries(payload).forEach(([k, v]) => fd.append(k, v));
        // Important: ne pas fixer Content-Type pour laisser le navigateur gérer le boundary
        res = await fetch(FORMSPREE_ENDPOINT, {
          method: 'POST',
          headers: { 'Accept': 'application/json' },
          body: fd
        });
        data = await res.json().catch(() => ({}));
      }

      if (res.ok) {
        setIsSubmitted(true);
        setFormData({
          name: "",
          email: "",
          phone: "",
          projectType: "",
          message: "",
          consent: false
        });
        setTimeout(() => setIsSubmitted(false), 5000);
      } else {
        if (data && data.errors) {
          setErrorMsg("Erreur Formspree : " + data.errors.map(e => e.message).join(", "));
        } else if (res.status === 403) {
          setErrorMsg("Accès refusé (403). Causes possibles : ID de formulaire incorrect, adresse non confirmée, domaine restreint ou quota atteint.");
        } else if (res.status === 422) {
          setErrorMsg("Données invalides (422) – vérifier les champs obligatoires.");
        } else {
          setErrorMsg(`Échec d'envoi (statut ${res.status}).`);
        }
      }
    } catch (err) {
      console.error('Erreur Formspree', err);
      setErrorMsg("Erreur réseau lors de l'envoi du formulaire.");
    }

    setIsLoading(false);
  };

  const projectTypes = [
    "Rénovation complète",
    "Extension", 
    "Aménagement intérieur",
    "Autre"
  ];

  return (
    <section id="contact" className="py-20 relative">
      {/* Image de fond */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1434030216411-0b793f4b4173?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80')"
        }}
      ></div>
      {/* Overlay semi-transparent */}
      <div className="absolute inset-0 bg-white/90"></div>
                    <p className="text-sm text-gray-500">Réponse sous 48h</p>
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Parlons de votre <span className="text-blue-900">projet</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Vous avez un projet en tête ? Contactez-moi pour un premier échange gratuit 
            et sans engagement.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Mes coordonnées
              </h3>
              <div className="space-y-6">
                <div className="flex items-center space-x-4 p-4 bg-slate-50 rounded-lg">
                  <div className="bg-blue-900 text-white p-3 rounded-full flex-shrink-0">
                    <Phone className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Téléphone</h4>
                    <p className="text-gray-600">{contactInfo.phone}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-4 p-4 bg-slate-50 rounded-lg">
                  <div className="bg-blue-900 text-white p-3 rounded-full flex-shrink-0">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Email</h4>
                    <p className="text-gray-600">{contactInfo.email}</p>
                    <p className="text-sm text-gray-500">Réponse sous 48h</p>
                  </div>
                </div>

                <div className="flex items-center space-x-4 p-4 bg-slate-50 rounded-lg">
                  <div className="bg-blue-900 text-white p-3 rounded-full flex-shrink-0">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Zone d'intervention</h4>
                    <p className="text-gray-600">{contactInfo.address}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-4 p-4 bg-slate-50 rounded-lg">
                  <div className="bg-blue-900 text-white p-3 rounded-full flex-shrink-0">
                    <Clock className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Horaires</h4>
                    <p className="text-gray-600">{contactInfo.hours}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Bloc "Pourquoi me contacter ?" supprimé à la demande */}
          </div>

          {/* Contact Form */}
          <div className="bg-slate-50 rounded-xl p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              Demande de contact
            </h3>

            {isSubmitted ? (
              <div className="text-center py-12" role="status" aria-live="polite">
                <div className="bg-green-100 text-green-800 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8" />
                </div>
                <h4 className="text-xl font-semibold text-gray-900 mb-2">
                  Message envoyé
                </h4>
                <p className="text-gray-600 mb-2">
                  Merci pour votre demande. Je reviens vers vous rapidement.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                {/* Honeypot anti-spam invisible */}
                <div style={{ display: 'none' }} aria-hidden="true">
                  <label>Ne pas remplir ce champ
                    <input
                      type="text"
                      name="_gotcha"
                      value={honeypot}
                      onChange={(e) => setHoneypot(e.target.value)}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </label>
                </div>
                {/* Hidden subject fallback (HTML submit non utilisé ici mais garde compat) */}
                <input type="hidden" name="_subject" value={`Contact CTB28 - ${formData.projectType || 'Demande'}`} />
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Nom complet *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-colors duration-200"
                      placeholder="Votre nom"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Téléphone *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-colors duration-200"
                      placeholder="07 65 63 25 84"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-colors duration-200"
                    placeholder="votre@email.fr"
                  />
                </div>

                <div>
                  <label htmlFor="projectType" className="block text-sm font-medium text-gray-700 mb-2">
                    Type de projet *
                  </label>
                  <select
                    id="projectType"
                    name="projectType"
                    value={formData.projectType}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-colors duration-200"
                  >
                    <option value="">Sélectionnez votre projet</option>
                    {projectTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Décrivez votre projet *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-colors duration-200 resize-none"
                    placeholder="Décrivez votre projet, vos attentes, votre budget approximatif, vos contraintes..."
                  ></textarea>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <input
                      id="consent"
                      name="consent"
                      type="checkbox"
                      checked={formData.consent}
                      onChange={handleInputChange}
                      required
                      className="mt-1 h-5 w-5 text-blue-900 border-gray-300 rounded focus:ring-blue-900"
                    />
                    <label htmlFor="consent" className="text-sm text-gray-600 leading-relaxed">
                      J'accepte que mes données soient utilisées pour être recontacté(e) concernant ma demande.
                      Voir notre <a href="/politique-confidentialite" className="underline text-blue-900">Politique de confidentialité</a>.
                    </label>
                  </div>

                  {errorMsg && (
                    <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3 text-center">
                      {errorMsg}
                    </div>
                  )}
                  <button
                  type="submit"
                  disabled={isLoading}
                  className={`w-full py-4 rounded-lg font-semibold transition-colors duration-200 flex items-center justify-center space-x-2 ${
                    isLoading 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-blue-900 text-white hover:bg-blue-800'
                  }`}
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                      <span>Envoi en cours...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      <span>Envoyer ma demande</span>
                    </>
                  )}
                </button>
                  <p className="text-xs text-gray-500 text-center">
                    * Champs obligatoires. Vos données ne sont utilisées que pour traiter votre demande.
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>

        {/* Étapes d'accompagnement (ajout pour réduire l'effet de vide) */}
        <div className="mt-24">
          <h3 className="text-2xl font-bold text-gray-900 text-center mb-10">Comment ça se passe ?</h3>
          <div className="grid md:grid-cols-3 gap-8">
            {[{
              titre: '1. Analyse',
              texte: 'Échange initial pour comprendre votre besoin, contraintes et objectifs.'
            }, {
              titre: '2. Planification',
              texte: 'Étude de faisabilité, priorisation, estimation et organisation des étapes.'
            }, {
              titre: '3. Suivi & Pilotage',
              texte: 'Contrôle de l’avancement, ajustements et validation jusqu’à la livraison.'
            }].map((etape, i) => (
              <div key={i} className="relative bg-white/70 backdrop-blur-sm border border-blue-100 rounded-xl p-6 shadow-sm hover:shadow-md transition">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-blue-900 text-white font-semibold text-lg mb-4">{i+1}</div>
                <h4 className="font-semibold text-gray-900 mb-2">{etape.titre}</h4>
                <p className="text-gray-600 text-sm leading-relaxed">{etape.texte}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-gray-500 text-sm mt-8">Un processus clair pour sécuriser vos décisions et optimiser votre budget.</p>
        </div>
      </div>
    </section>
  );
};

export default Contact;